import type { CategorizedHPOOption } from '@/types/RequisitionFormTypes'
import { extractHPOId, isHpoCode } from './hpoTypes'

/**
 * Normalize an HPO id/label to canonical `HP:XXXXXXX` form.
 * Handles obo URLs, `HP_` underscore form, `HP:` colon form, and bare 7-digit
 * ids. Returns the trimmed input unchanged when no HPO pattern is detected.
 */
export const normalizeHpoId = (value?: string): string | undefined => {
  if (!value) {
    return value
  }
  const trimmed = value.trim()
  if (!trimmed) {
    return trimmed
  }
  if (/^HP:\d+$/i.test(trimmed)) {
    return trimmed
  }
  const underscoreMatch = trimmed.match(/HP_\d+/i)
  if (underscoreMatch) {
    return underscoreMatch[0].replace('_', ':')
  }
  const colonMatch = trimmed.match(/HP:\d+/i)
  if (colonMatch) {
    return colonMatch[0]
  }
  const digitsMatch = trimmed.match(/\b(\d{7})\b/)
  if (digitsMatch) {
    return `HP:${digitsMatch[1]}`
  }
  return trimmed
}

export type GetHPOTermById = (id: string) => Promise<{ id: string; lbl: string } | null>

/**
 * Resolve a human-readable label for a (possibly legacy/code-only) HPO option.
 * Tries the term id, then the node id, then the label-as-code against the
 * terminology service; falls back to the node label or the original label.
 */
export const resolveHpoLabel = async (
  term: CategorizedHPOOption,
  getTermById: GetHPOTermById
): Promise<{ id: string; label: string }> => {
  const normalizedId = normalizeHpoId(term.id) ?? term.id
  const normalizedLabel = normalizeHpoId(term.label) ?? term.label
  const normalizedNodeId = normalizeHpoId(term.node?.id) ?? term.node?.id
  const nodeLabel = term.node?.lbl

  const primaryNode = await getTermById(normalizedId)
  const nodeFromNodeId = !primaryNode && normalizedNodeId ? await getTermById(normalizedNodeId) : null
  const fallbackNode =
    !primaryNode && !nodeFromNodeId && isHpoCode(normalizedLabel) ? await getTermById(normalizedLabel) : null
  const node = primaryNode ?? nodeFromNodeId ?? fallbackNode

  if (node) {
    return { id: extractHPOId(node.id), label: node.lbl }
  }

  if (nodeLabel && !isHpoCode(nodeLabel)) {
    return { id: normalizedId, label: nodeLabel }
  }

  return { id: normalizedId, label: term.label }
}
